import { useQuery } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase'
import type { Programa, Turma } from '@/tipos/tabelas'

/**
 * Peças compartilhadas pelas seções da administração.
 */

export const CHAVES_ADMIN = {
  programa: ['admin', 'programa'] as const,
  turmas: ['admin', 'turmas'] as const,
  participantes: ['admin', 'participantes'] as const,
  dias: (programaId: string) => ['admin', 'dias', programaId] as const,
  videos: (programaId: string) => ['admin', 'videos', programaId] as const,
  cardapios: (programaId: string, periodo: number) =>
    ['admin', 'cardapios', programaId, periodo] as const,
}

export interface TurmaComContagem extends Turma {
  matriculas: { count: number }[]
}

/**
 * Turmas, únicas para toda a administração.
 *
 * Antes, cada seção fazia sua própria busca de `turmas` sob a MESMA
 * chave de cache (`CHAVES_ADMIN.turmas`), mas com formatos diferentes —
 * uma trazia a contagem de matrículas embutida, outras não, uma
 * filtrava só as ativas. Como o React Query identifica uma consulta
 * pela chave, não pelo formato do retorno, a segunda tela a montar
 * herdava silenciosamente o resultado da primeira: ao entrar em
 * Participantes e depois em Turmas, esta última recebia dados sem o
 * campo `matriculas`, tentava ler `turma.matriculas[0].count` e
 * quebrava — sem nenhum erro visível, só a tela em branco.
 *
 * Uma consulta só, com tudo que qualquer seção pode precisar; cada
 * seção filtra na memória o que for do seu interesse.
 */
export function useTurmasAdmin() {
  return useQuery({
    queryKey: CHAVES_ADMIN.turmas,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('turmas')
        .select('*, matriculas(count)')
        .order('data_inicio', { ascending: false })

      if (error) throw error
      return data as TurmaComContagem[]
    },
  })
}

/**
 * O programa em edição.
 *
 * Hoje só existe o Projeto Viver, então a administração assume o
 * primeiro programa ativo. Quando entrar um segundo (seção 15), isto
 * vira um seletor — o schema já suporta, é só a interface que muda.
 */
export function useProgramaAtual() {
  return useQuery({
    queryKey: CHAVES_ADMIN.programa,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('programas')
        .select('*')
        .eq('ativo', true)
        .order('criado_em')
        .limit(1)
        .maybeSingle()

      if (error) throw error
      return data as Programa | null
    },
    staleTime: 30 * 60 * 1000,
  })
}

/**
 * Extrai o ID de um vídeo do YouTube a partir de qualquer formato de URL.
 *
 * A Raquel vai colar o link inteiro que copiou do navegador ou do botão
 * de compartilhar; exigir que ela extraia o ID à mão seria uma fonte de
 * erro garantida.
 */
export function extrairIdYouTube(entrada: string): string | null {
  const texto = entrada.trim()
  if (!texto) return null

  // Já é um ID solto
  if (/^[\w-]{11}$/.test(texto)) return texto

  const padroes = [
    /(?:youtube\.com\/watch\?(?:.*&)?v=)([\w-]{11})/,
    /(?:youtu\.be\/)([\w-]{11})/,
    /(?:youtube\.com\/embed\/)([\w-]{11})/,
    /(?:youtube\.com\/shorts\/)([\w-]{11})/,
    /(?:youtube\.com\/live\/)([\w-]{11})/,
  ]

  for (const padrao of padroes) {
    const achado = texto.match(padrao)
    if (achado?.[1]) return achado[1]
  }

  return null
}
